import React, { useState } from 'react';
import {
  Box, Button, FormControl, InputLabel, Select, MenuItem,
  Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, ToggleButton, ToggleButtonGroup, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, IconButton, TablePagination
} from '@mui/material';
import { Share, ContentCopy } from '@mui/icons-material';
import { timesheetService, userService, projectService } from '../services/api';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { format, startOfWeek, parseISO } from 'date-fns';
import { usePagination } from '../hooks/usePagination';

const Reports: React.FC = () => {
  const [reportType, setReportType] = useState<'monthly' | 'weekly'>('monthly');
  
  // Monthly report state
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  
  // Weekly report state
  const [weekStartDate, setWeekStartDate] = useState(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  
  // Common state
  const [userId, setUserId] = useState<number | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  
  // Share link state
  const [shareLinkDialog, setShareLinkDialog] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLinkMessage, setShareLinkMessage] = useState('');

  React.useEffect(() => {
    loadUsers();
    loadProjects();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await userService.getDevelopers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const generateReport = async () => {
    try {
      let data;
      if (reportType === 'monthly') {
        data = await timesheetService.getMonthlyReport(month, year, userId || undefined, projectId || undefined);
      } else {
        data = await timesheetService.getWeeklyReport(weekStartDate, userId || undefined, projectId || undefined);
      }
      setReportData(data);
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const getReportTitle = () => {
    if (reportType === 'monthly') {
      return `Timesheet Report - ${month}/${year}`;
    } else {
      // Use report data if available for accurate dates, parse with parseISO for timezone safety
      if (reportData && reportData.weekStart && reportData.weekEnd) {
        return `Timesheet Report - Week ${format(parseISO(reportData.weekStart), 'MMM dd')} to ${format(parseISO(reportData.weekEnd), 'MMM dd, yyyy')}`;
      }
      return `Timesheet Report - Weekly`;
    }
  };

  const exportToPDF = () => {
    if (!reportData) return;
    const doc = new jsPDF();
    doc.text(getReportTitle(), 10, 10);
    doc.text(`Total Hours: ${reportData.totalHours}`, 10, 20);
    doc.text(`Total Entries: ${reportData.totalEntries}`, 10, 30);
    
    const filename = reportType === 'monthly' 
      ? `timesheet-report-${month}-${year}.pdf`
      : `timesheet-report-week-${weekStartDate}.pdf`;
    doc.save(filename);
  };

  const exportToExcel = () => {
    if (!reportData) return;
    const ws = XLSX.utils.json_to_sheet(reportData.timesheets);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheets');
    
    const filename = reportType === 'monthly'
      ? `timesheet-report-${month}-${year}.xlsx`
      : `timesheet-report-week-${weekStartDate}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const handleReportTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: 'monthly' | 'weekly' | null) => {
    if (newType !== null) {
      setReportType(newType);
      setReportData(null); // Clear previous report data
    }
  };

  const handleGenerateShareLink = async () => {
    if (!userId || reportType !== 'monthly') {
      setShareLinkMessage('Please select an employee and use monthly report type to generate share link');
      return;
    }

    try {
      const response = await timesheetService.generateShareLink(userId, month, year, 30); // 30 days expiry
      setShareLink(response.shareUrl);
      setShareLinkDialog(true);
      setShareLinkMessage(`Link expires on: ${new Date(response.expiresAt).toLocaleDateString()}`);
    } catch (error: any) {
      setShareLinkMessage(error.response?.data?.message || 'Error generating share link');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setShareLinkMessage('Link copied to clipboard!');
  };

  const { pagedRows: pagedTimesheets, paginationProps: timesheetsPaginationProps } = usePagination(reportData?.timesheets ?? []);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Reports
        </Typography>
        <ToggleButtonGroup
          value={reportType}
          exclusive
          onChange={handleReportTypeChange}
          aria-label="report type"
        >
          <ToggleButton value="monthly" aria-label="monthly">
            Monthly
          </ToggleButton>
          <ToggleButton value="weekly" aria-label="weekly">
            Weekly
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {reportType === 'monthly' ? (
          <>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} onChange={(e) => setMonth(Number(e.target.value))} label="Month">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                  <MenuItem key={m} value={m}>
                    {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} onChange={(e) => setYear(Number(e.target.value))} label="Year">
                {[2023, 2024, 2025, 2026].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <TextField
            label="Week Start Date (Monday)"
            type="date"
            value={weekStartDate}
            onChange={(e) => setWeekStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
        )}

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Developer</InputLabel>
          <Select value={userId || ''} onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : null)} label="Developer">
            <MenuItem value="">All</MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>{user.fullName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Project</InputLabel>
          <Select value={projectId || ''} onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : null)} label="Project">
            <MenuItem value="">All</MenuItem>
            {projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" onClick={generateReport} sx={{ alignSelf: 'flex-end' }}>
          Generate Report
        </Button>
      </Box>

      {reportData && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>{getReportTitle()}</Typography>
            <Typography variant="body1">Total Hours: {reportData.totalHours}</Typography>
            <Typography variant="body1">Total Entries: {reportData.totalEntries}</Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={exportToPDF} sx={{ mr: 2 }}>
                Export to PDF
              </Button>
              <Button variant="outlined" onClick={exportToExcel} sx={{ mr: 2 }}>
                Export to Excel
              </Button>
              {reportType === 'monthly' && userId && (
                <Button 
                  variant="outlined" 
                  startIcon={<Share />}
                  onClick={handleGenerateShareLink}
                  color="primary"
                >
                  Generate Share Link
                </Button>
              )}
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedTimesheets.map((ts: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{format(new Date(ts.date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>{ts.userName}</TableCell>
                    <TableCell>{ts.projectName}</TableCell>
                    <TableCell>{ts.taskDescription}</TableCell>
                    <TableCell>{ts.hours}</TableCell>
                    <TableCell>{ts.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {reportData.timesheets.length > 0 && <TablePagination {...timesheetsPaginationProps} />}
          </TableContainer>
        </>
      )}

      <Dialog open={shareLinkDialog} onClose={() => setShareLinkDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Public Share Link Generated</DialogTitle>
        <DialogContent>
          {shareLinkMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {shareLinkMessage}
            </Alert>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              value={shareLink}
              InputProps={{
                readOnly: true,
              }}
              multiline
              rows={2}
            />
            <IconButton onClick={handleCopyLink} color="primary">
              <ContentCopy />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Share this link with anyone to view the monthly timesheet report. The link will expire in 30 days.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareLinkDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reports;
